import { Container, Graphics, Text } from 'pixi.js';
import type { Scene } from '../core/Scene';
import type { InputSnapshot } from '../input/actions';
import type { GameContext } from '../GameContext';
import { Palette } from '../render/Palette';
import type { RunState } from '../sim/run/RunState';
import type { Minimap } from '../ui/Minimap';
import { CHARACTERS } from '../sim/characters/CharacterDefs';
import { ACTIVE_DEFS } from '../sim/characters/actives';
import { skillOutgoingDamageMult } from '../sim/abilities/skillHooks';
import { PICKUP_BUMP_CAP } from '../sim/run/pickupTrack';
import { HUD_MINIMAP } from './RunScene';

/** Enlarged width of the borrowed floor map (HUD size is 150). */
const PAUSE_MAP_W = 300;

/** Pause: resume or abandon, the floor map (round 5), and the stat sheet. */
export class PauseOverlay implements Scene {
  readonly root = new Container();
  readonly blocksUpdateBelow = true;

  private focus = 0;
  private labels: Text[] = [];
  private acceptDelay = 8;
  /** Borrowed-minimap restore state (the RunScene instance is reparented). */
  private mapHome: { parent: Container; x: number; y: number; alpha: number } | null = null;

  constructor(
    private readonly ctx: GameContext,
    private readonly onAbandon: () => void,
    seed: string,
    floor: number,
    runState: RunState,
    private readonly minimap: Minimap | null = null,
    private readonly playerPos: { x: number; y: number } = { x: 0, y: 0 },
  ) {
    const w = ctx.app.screen.width;
    const h = ctx.app.screen.height;
    this.root.addChild(new Graphics().rect(0, 0, w, h).fill({ color: 0x05060c, alpha: 0.75 }));

    const title = new Text({
      text: 'PAUSED',
      style: {
        fontFamily: 'monospace', fontSize: 36, fontWeight: 'bold',
        fill: Palette.hudText, letterSpacing: 6,
      },
    });
    title.anchor.set(0.5);
    title.position.set(w / 2, h / 2 - 150);
    this.root.addChild(title);

    const charDef = CHARACTERS[runState.character];
    const active = ACTIVE_DEFS[runState.character];
    const info = new Text({
      text: `${charDef.name}   —   floor ${floor}   seed ${seed}` +
        (charDef.aspectName !== null ? `\n${charDef.aspectName}: ${charDef.aspectBlurb ?? ''}` : '') +
        `\nActive — ${active.name}: ${active.blurb}`,
      style: {
        fontFamily: 'monospace', fontSize: 13, fill: Palette.hudDim,
        align: 'center', lineHeight: 19, wordWrap: true, wordWrapWidth: Math.min(680, w - 80),
      },
    });
    info.anchor.set(0.5, 0);
    info.position.set(w / 2, h / 2 - 132);
    this.root.addChild(info);

    ['Resume', 'Abandon Run'].forEach((label, i) => {
      const text = new Text({
        text: label,
        style: { fontFamily: 'monospace', fontSize: 18, fill: Palette.hudText },
      });
      text.anchor.set(0.5);
      // Sits below the info block even for wordy aspects (Trine wraps to 5
      // lines) — the old h/2 − 30 start collided with them.
      text.position.set(w / 2, h / 2 - 12 + i * 36);
      this.labels.push(text);
      this.root.addChild(text);
    });

    // The character stat sheet: resolved live values, two columns.
    const stats = runState.stats;
    const dmg = stats.resolve('damageMult') * skillOutgoingDamageMult(runState);
    const rows: [string, string][] = [
      ['damage', `×${dmg.toFixed(2)}`],
      ['attack speed', `×${(1 / stats.resolve('cooldownMult')).toFixed(2)}`],
      ['area', `×${stats.resolve('areaMult').toFixed(2)}`],
      ['proj speed', `×${stats.resolve('projSpeedMult').toFixed(2)}`],
      ['move speed', `${Math.round(stats.resolve('moveSpeed'))} px/s`],
      ['max HP', `${Math.round(stats.resolve('maxHp'))}`],
      ['magnet', `${Math.round(stats.resolve('magnetRange'))} px`],
      ['gem gain', `×${stats.resolve('gemGainMult').toFixed(2)}`],
      ['bar fill', `×${stats.resolve('killFillMult').toFixed(2)}`],
      ['pickup rate', `×${stats.resolve('pickupRateMult').toFixed(2)}`],
      ['kills', `${runState.kills}`],
      ['stat bumps', `${runState.pickupBumpsGranted}/${PICKUP_BUMP_CAP}`],
    ];
    const colW = 250;
    const half = Math.ceil(rows.length / 2);
    const panelW = colW * 2 + 40;
    // With the floor map parked on the left (round 5), the stat panel keeps
    // clear of it; without a map the old centered layout holds.
    const panelX = this.minimap
      ? Math.max((w - panelW) / 2, 40 + PAUSE_MAP_W + 40)
      : (w - panelW) / 2;
    const panelY = h / 2 + 62;
    const panelH = half * 22 + 28;
    this.root.addChild(new Graphics()
      .roundRect(panelX, panelY, panelW, panelH, 8)
      .fill({ color: Palette.hudPanel, alpha: 0.7 })
      .stroke({ width: 1.5, color: Palette.hudDim, alpha: 0.35 }));
    rows.forEach(([label, value], i) => {
      const col = i < half ? 0 : 1;
      const row = i % half;
      const line = new Text({
        text: `${label.padEnd(13)}${value}`,
        style: { fontFamily: 'monospace', fontSize: 13, fill: Palette.hudText },
      });
      line.position.set(panelX + 24 + col * (colW + 12), panelY + 14 + row * 22);
      this.root.addChild(line);
    });
  }

  /** Borrow the RunScene's live minimap, enlarged — shared fog for free. */
  enter(): void {
    const m = this.minimap;
    if (!m) return;
    this.mapHome = {
      parent: m.root.parent as Container,
      x: m.root.x, y: m.root.y, alpha: m.root.alpha,
    };
    m.setWidth(PAUSE_MAP_W);
    const h = this.ctx.app.screen.height;
    m.root.position.set(40, Math.max(24, h / 2 - m.displayHeight / 2));
    m.root.alpha = 1;
    m.root.visible = true;
    this.root.addChild(m.root);
    m.sync(this.playerPos.x, this.playerPos.y); // setWidth beat the throttle
  }

  /** Hand the map back to the HUD exactly as it was (hidden per the A/B flag). */
  exit(): void {
    const m = this.minimap;
    if (!m || !this.mapHome) return;
    m.setWidth(150);
    m.root.position.set(this.mapHome.x, this.mapHome.y);
    m.root.alpha = this.mapHome.alpha;
    m.root.visible = HUD_MINIMAP;
    this.mapHome.parent.addChild(m.root);
    m.sync(this.playerPos.x, this.playerPos.y);
    this.mapHome = null;
  }

  update(_dt: number, input: InputSnapshot): void {
    if (this.acceptDelay > 0) {
      this.acceptDelay--;
      return;
    }
    if (input.navY !== 0) {
      this.focus = (this.focus + input.navY + this.labels.length) % this.labels.length;
    }
    if (input.pausePressed || input.cancelPressed) {
      this.ctx.scenes.pop();
      return;
    }
    if (input.confirmPressed) {
      this.ctx.scenes.pop();
      if (this.focus === 1) this.onAbandon();
    }
  }

  render(_alpha: number): void {
    this.labels.forEach((text, i) => {
      text.text = (this.focus === i ? '▸ ' : '  ') + (i === 0 ? 'Resume' : 'Abandon Run');
      text.style.fill = this.focus === i ? Palette.playerCool : Palette.hudText;
    });
  }
}
